import { Box, constants, Stack, Text } from '@redbubble/design-system';
import { useMemo } from 'react';

import styles from './Indicator.module.scss';

export interface IndicatorProps {
  value: number;
  title: string;
  description: string;
  minLegend: string;
  averageLegend: string;
  maxLegend: string;
  minValue?: number;
  maxValue?: number;
}

const INDICATOR_PERCENTAGE_WIDTH = 6;
const MAX_PERCENTAGE = 100;
const MIN_PERCENTAGE = 0;

export const Indicator = ({
  value,
  title,
  description,
  minLegend,
  averageLegend,
  maxLegend,
  minValue = 1,
  maxValue = 10,
}: IndicatorProps) => {
  const getIndicatorPosition = useMemo(() => {
    const scaleIntervals = maxValue - minValue;
    const offset = INDICATOR_PERCENTAGE_WIDTH / 2;
    const indicatorPositionOnScale =
      ((value - minValue) / (maxValue - minValue)) * scaleIntervals;
    const percentagePosition =
      (indicatorPositionOnScale / scaleIntervals) * MAX_PERCENTAGE - offset;

    // check if the element position fits on the baseline
    const maxPosition = MAX_PERCENTAGE - INDICATOR_PERCENTAGE_WIDTH;

    if (percentagePosition > maxPosition) {
      return maxPosition;
    }

    if (percentagePosition < MIN_PERCENTAGE) {
      return 0;
    }
    return percentagePosition;
  }, [value, minValue, maxValue]);

  return (
    <Box>
      <Stack direction={constants.VERTICAL}>
        <Text type="display5" element="h3">
          {title}
        </Text>
        <Text type="body2">{description}</Text>
      </Stack>
      <Box className={styles.indicatorSlider}>
        <div
          style={{
            width: `${INDICATOR_PERCENTAGE_WIDTH}%`,
            left: `${getIndicatorPosition}%`,
          }}
          className={styles.indicator}
        />
        <div className={styles.baseline} />
        <div className={styles.legend}>
          <Text type="display6" className={styles.legendText}>
            {minLegend}
          </Text>
          <Text type="display6" className={styles.legendText}>
            {averageLegend}
          </Text>
          <Text type="display6" className={styles.legendText}>
            {maxLegend}
          </Text>
        </div>
      </Box>
    </Box>
  );
};
